import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";

// =============================================================================
// useForecastData — Forecast & Planning Data
// =============================================================================
// PURPOSE: Fetches all data for the forecast tab in one RPC call:
// stagger chart rows, monthly actuals, daily cumulative, current month metrics.
// No date range dependency — forecast always shows full history.
// =============================================================================

export interface ForecastCell {
  predicted: number;
  actual: number | null;
  metric: string;
  for_month: string;
}

export interface StaggerRow {
  forecast_made_month: string;
  forecasts: Record<string, ForecastCell>;
}

export interface MonthlyActual {
  month: string;
  revenue: number;
  cash: number;
  deals: number;
}

export interface DailyPoint {
  day: string;
  cum_revenue: number;
  cum_cash: number;
}

export interface CurrentMonthMetrics {
  days_elapsed: number;
  days_remaining: number;
  days_in_month: number;
  revenue_so_far: number;
  cash_so_far: number;
  deals_so_far: number;
  daily_avg_revenue: number;
  daily_avg_cash: number;
}

export interface ForecastData {
  stagger: StaggerRow[];
  monthlyActuals: MonthlyActual[];
  dailyCumulative: DailyPoint[];
  currentMonthMetrics: CurrentMonthMetrics;
  isLoading: boolean;
}

const EMPTY_METRICS: CurrentMonthMetrics = {
  days_elapsed: 0,
  days_remaining: 0,
  days_in_month: 0,
  revenue_so_far: 0,
  cash_so_far: 0,
  deals_so_far: 0,
  daily_avg_revenue: 0,
  daily_avg_cash: 0,
};

// Combined state interface — batches all forecast data into a single setState to avoid
// 4 separate re-renders when the RPC resolves.
interface ForecastState {
  stagger: StaggerRow[];
  monthlyActuals: MonthlyActual[];
  dailyCumulative: DailyPoint[];
  currentMonthMetrics: CurrentMonthMetrics;
  isLoading: boolean;
}

const INITIAL_FORECAST_STATE: ForecastState = {
  stagger: [],
  monthlyActuals: [],
  dailyCumulative: [],
  currentMonthMetrics: EMPTY_METRICS,
  isLoading: false,
};

export function useForecastData(organizationId: string | undefined | null): ForecastData {
  const [forecastState, setForecastState] = useState<ForecastState>(INITIAL_FORECAST_STATE);
  const abortRef = useRef<AbortController | null>(null);

  const fetchForecast = useCallback(async () => {
    if (!organizationId || !supabase) {
      setForecastState(INITIAL_FORECAST_STATE);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setForecastState((prev) => ({ ...prev, isLoading: true }));

    try {
      const { data, error } = await supabase.rpc("get_forecast_data", {
        p_organization_id: organizationId,
      });

      if (controller.signal.aborted) return;

      if (error) {
        console.warn("Forecast data error:", error);
        setForecastState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      if (data) {
        // Single setState — one re-render instead of 4
        setForecastState({
          stagger: data.stagger || [],
          monthlyActuals: data.monthly_actuals || [],
          dailyCumulative: data.daily_cumulative || [],
          currentMonthMetrics: data.current_month_metrics || EMPTY_METRICS,
          isLoading: false,
        });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.warn("Forecast data fetch failed:", err);
      setForecastState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [organizationId]);

  useEffect(() => {
    fetchForecast();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchForecast]);

  return forecastState;
}
